'use client';

import { css } from '@bts-web/utils-style-engine';
import { ComponentProps, FC } from 'react';
import { ButtonBase } from '../ButtonBase/ButtonBase';
import { SetupTwoFaModal } from './twoFaModals/SetupTwoFaModal/SetupTwoFaModal';
import { UserAccessModal } from './UserAccessModal/UserAccessModal';
import { TriggerResetTwoFaModal } from './twoFaModals/TriggerResetTwoFaModal/TriggerResetTwoFaModal';
import { ResetTwoFaModal } from './twoFaModals/ResetTwoFaModal/ResetTwoFaModal';
import { TriggerSetupTwoFaModal } from './twoFaModals/TriggerSetupTwoFaModal/TriggerSetupTwoFaModal';
import { AccessInfoType } from '../../utils/userAccess';
import { SetupFATCAModal } from '../../../fatca/SetupFATCAModal';
import {
  ModalComponent,
  useModalDrawerContext,
} from '../ModalDrawerContext/ModalDrawerContext';
import { DialogBase } from '../DialogBase/DialogBase';
import { CryptoTransferTransactionStatusModal } from '../../../crypto-transfer-details';
import { WarningModal } from '../modals/WarningModal';
import { FinishTradeStatusModal } from '../../../trade/FinishTrade/FinishTradeStatusModal/FinishTradeStatusModal';
import { SavingsPlanCancelModal } from '../../../savings-plans/savings-plan-details/components/SavingsPlanCancelModal/SavingsPlanCancelModal';

const swapOfferMockData = {
  id: 'U3dhcE9mZmVyOjAxOTljMmMzLTYzMGMtNzM0Ni1iNmI1LTgxMGEwN2VhYWZjNw',
  expiresAt: '2025-10-08T07:40:21Z',
  sourceAsset: {
    __typename: 'StockAsset',
    id: 'U3RvY2tBc3NldDplYThjMjJmYi1lZGVlLTExZWItOWJmMC0wNjUwMmIxZmU1NWQ',
    name: 'Microsoft',
    price: {
      value: '451.2750000000000000',
      precision: 2,
    },
    logoUrl:
      'https://cdn.cms.staging.waskurzes.com/logos/v4/asset/light/ea8c22fb-edee-11eb-9bf0-06502b1fe55d.png',
    symbol: 'MSFT',
  },
  targetAsset: {
    __typename: 'MetalAsset',
    id: 'TWV0YWxBc3NldDplYTg5YTM1My1lZGVlLTExZWItOWJmMC0wNjUwMmIxZmU1NWQ',
    name: 'Gold',
    price: {
      value: '111.5994117300000000',
      precision: 2,
    },
    logoUrl:
      'https://cdn.cms.staging.waskurzes.com/logos/v4/asset/light/ea89a353-edee-11eb-9bf0-06502b1fe55d.png',
    symbol: 'XAU',
    legalUrl: 'https://bts-legal-documents.bitpanda.dev/en/rlb/AT',
  },
  sourceAmount: {
    value: '5.44110247',
    precision: 8,
  },
  targetAmount: {
    value: '21.54172454',
    precision: 8,
  },
  assetNetConversionRate: {
    value: '111.97283836',
    precision: 8,
  },
  feeAmount: {
    value: '42.13',
    precision: 2,
  },
  taxAmount: {
    value: '0.00',
    precision: 2,
  },
};

const mockTransactions = {
  done: 'Done',
  reset2faTitle: 'Reset two-factor authentication',
  reset2faSuccessDescription: 'A new two-factor authentication is enabled',
  reset2faSuccessDescriptionBody:
    'Crypto withdrawals are now secured with this new 2FA.',
  reset2faSkip: 'Changed my mind',
  reset2faDescription:
    'Resetting 2FA means replacing the current authenticator method used for verifying your identity when using features such as crypto withdrawals.',
  reset2faAreYouSure: 'Are you sure you want to proceed?',
  reset2faSelectMethodTitle: 'Which method would you like to use?',
  reset2faOptionAuthenticator: 'Use code from authenticator',
  reset2faOptionRecoveryCode: 'Use recovery code',
  reset2faOptionHelpdesk: `Don't have access to your codes? Please contact our helpdesk`,
  reset2faAppcodeTitle: 'Enter the verification code',
  reset2faAppcodeDescription:
    'Just type in the 6-digit verification code which was generated by your authenticator app.',
  reset2faInvalidCode: 'Invalid code, please try again',
  reset2faButton: 'Reset 2FA',
  reset2faRecoveryTitle: 'Enter the recovery code',
  reset2faRecoveryDescription:
    'Just type in one of your recovery codes that were generated during the 2FA set up.',
  generalRecoveryCode: 'General recovery code',
  qr2faSubtitle: 'Scan the QR code with your authenticator app',
  qr2faManualDescription:
    'Alternatively you can enter the code below in your authenticator app:',
  qr2faDescription:
    'Use or install any TOTP authenticator app on your phone or computer and scan the QR code',
  corpOnboardingEmailQTitle: 'Email',
  authenticationText: 'Please enter the code on your authenticator app.',
  recovery2faSubtitle: 'Save your recovery code now',
  recovery2faBody:
    'You will need your recovery code to connect new devices to your account.',
  recovery2faInputfield: 'Your recovery code',
  copy: 'Copy',
  continue: 'Continue',
  recovery2faWarning:
    'Make sure you write down or print out the code and store it somewhere safe. Do not share it with anyone.',
  welcome2faTitle: 'Two-factor authentication (2FA)',
  welcome2faSubtitle: 'Enhance your security',
  welcome2faBody:
    'Two-factor authentication (2FA) increases the security of your account. 2FA is a prerequisite for using features such as crypto withdrawals. Use or install any TOTP authenticator app on your phone or computer and scan the QR code. Please also remember to save the backup code when setting up 2FA.',
  welcome2faCtaSkip: 'Set up later',
  generalErrorHeadline: 'Sorry, something went wrong on our end',
};

const ModalComponentWithModalChild: ModalComponent = ({ onClose }) => {
  const { showModal } = useModalDrawerContext();

  return (
    <DialogBase id="modal-with-child" size="medium" onClose={onClose}>
      <div>
        <p>Modal with child</p>
        <ButtonBase onClick={() => showModal(SetupFATCAModal)}>
          Open Setup FATCA Modal
        </ButtonBase>
      </div>
    </DialogBase>
  );
};

const VeryTallModal: ModalComponent = ({ onClose }) => {
  return (
    <DialogBase
      id="very-tall-modal"
      size="medium"
      onClose={onClose}
      contentProps={{ disableContentPadding: true }}
    >
      <div
        className={css({
          height: '200vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        })}
      >
        <p>Very tall modal</p>
        <p>
          Lorem, ipsum dolor sit amet consectetur adipisicing elit. Nam atque
          quisquam omnis deserunt aliquid minus pariatur itaque vitae voluptatum
          perferendis sapiente, animi rem eveniet non, doloribus nobis,
          architecto quod fugit blanditiis. Ut temporibus aut voluptatibus
          impedit nesciunt? Sit consectetur in veritatis aliquid velit id
          mollitia temporibus ullam. Nihil, molestiae doloremque! Ipsam
          voluptatum et nesciunt, voluptatibus dolore quidem minus sequi eveniet
          dignissimos doloremque dolorum deleniti! Iusto reiciendis eos libero?
          Pariatur molestias eligendi explicabo odit ipsam atque assumenda
          laboriosam? Omnis optio, error eaque excepturi incidunt dolores quos
          at. Saepe harum debitis perspiciatis nisi optio. Consequuntur
          veritatis, laboriosam unde iste ad omnis vero neque natus ut,
          architecto atque magnam ipsum, sequi quibusdam ullam. Pariatur,
          quaerat. Mollitia, optio earum. Obcaecati aliquam ea repudiandae
          debitis distinctio iusto a unde temporibus magni, officiis ratione sit
          nemo minima voluptatibus corrupti provident quasi numquam cupiditate
          autem officia commodi laudantium alias adipisci omnis? Aspernatur
          veniam reprehenderit aliquam vitae enim cumque at facere dicta
          voluptatem ad beatae assumenda, perspiciatis ex, explicabo delectus
          quia, commodi laborum corporis officia error vero culpa? Totam
          asperiores molestias minima odit itaque dignissimos illo ipsum
          cupiditate maiores, facere dolore, optio deserunt maxime quidem
          distinctio. Soluta, inventore adipisci? Deleniti odio rerum minima,
          animi magnam error? Dignissimos adipisci officiis dicta perferendis
          neque debitis aliquid perspiciatis voluptatum natus illum eveniet,
          molestiae vel animi. Ab itaque officiis expedita fugit sapiente quas
          tenetur quisquam voluptatem excepturi doloribus ullam odit consectetur
          cum eos quibusdam odio doloremque voluptate, iusto facilis ratione
          provident pariatur? Minima vero excepturi id, doloremque ullam non
          eaque unde sunt, ratione cum autem aut. Exercitationem amet quo,
          maxime natus cupiditate commodi autem. Eum debitis reprehenderit enim
          suscipit dolores illum, neque voluptatibus quas minus? Quia, nisi
          incidunt? Optio explicabo minima qui praesentium magnam quisquam
          labore sapiente, rem unde eligendi quia aliquid porro esse ipsa quasi
          repellat cum. Sint accusamus iusto recusandae.
        </p>
        <p>
          Lorem, ipsum dolor sit amet consectetur adipisicing elit. Nam atque
          quisquam omnis deserunt aliquid minus pariatur itaque vitae voluptatum
          perferendis sapiente, animi rem eveniet non, doloribus nobis,
          architecto quod fugit blanditiis. Ut temporibus aut voluptatibus
          impedit nesciunt? Sit consectetur in veritatis aliquid velit id
          mollitia temporibus ullam. Nihil, molestiae doloremque! Ipsam
          voluptatum et nesciunt, voluptatibus dolore quidem minus sequi eveniet
          dignissimos doloremque dolorum deleniti! Iusto reiciendis eos libero?
          Pariatur molestias eligendi explicabo odit ipsam atque assumenda
          laboriosam? Omnis optio, error eaque excepturi incidunt dolores quos
          at. Saepe harum debitis perspiciatis nisi optio. Consequuntur
          veritatis, laboriosam unde iste ad omnis vero neque natus ut,
          architecto atque magnam ipsum, sequi quibusdam ullam. Pariatur,
          quaerat. Mollitia, optio earum. Obcaecati aliquam ea repudiandae
          debitis distinctio iusto a unde temporibus magni, officiis ratione sit
          nemo minima voluptatibus corrupti provident quasi numquam cupiditate
          autem officia commodi laudantium alias adipisci omnis? Aspernatur
          veniam reprehenderit aliquam vitae enim cumque at facere dicta
          voluptatem ad beatae assumenda, perspiciatis ex, explicabo delectus
          quia, commodi laborum corporis officia error vero culpa? Totam
          asperiores molestias minima odit itaque dignissimos illo ipsum
          cupiditate maiores, facere dolore, optio deserunt maxime quidem
          distinctio. Soluta, inventore adipisci? Deleniti odio rerum minima,
          animi magnam error? Dignissimos adipisci officiis dicta perferendis
          neque debitis aliquid perspiciatis voluptatum natus illum eveniet,
          molestiae vel animi. Ab itaque officiis expedita fugit sapiente quas
          tenetur quisquam voluptatem excepturi doloribus ullam odit consectetur
          cum eos quibusdam odio doloremque voluptate, iusto facilis ratione
          provident pariatur? Minima vero excepturi id, doloremque ullam non
          eaque unde sunt, ratione cum autem aut. Exercitationem amet quo,
          maxime natus cupiditate commodi autem. Eum debitis reprehenderit enim
          suscipit dolores illum, neque voluptatibus quas minus? Quia, nisi
          incidunt? Optio explicabo minima qui praesentium magnam quisquam
          labore sapiente, rem unde eligendi quia aliquid porro esse ipsa quasi
          repellat cum. Sint accusamus iusto recusandae.
        </p>
        <p>
          Lorem, ipsum dolor sit amet consectetur adipisicing elit. Nam atque
          quisquam omnis deserunt aliquid minus pariatur itaque vitae voluptatum
          perferendis sapiente, animi rem eveniet non, doloribus nobis,
          architecto quod fugit blanditiis. Ut temporibus aut voluptatibus
          impedit nesciunt? Sit consectetur in veritatis aliquid velit id
          mollitia temporibus ullam. Nihil, molestiae doloremque! Ipsam
          voluptatum et nesciunt, voluptatibus dolore quidem minus sequi eveniet
          dignissimos doloremque dolorum deleniti! Iusto reiciendis eos libero?
          Pariatur molestias eligendi explicabo odit ipsam atque assumenda
          laboriosam? Omnis optio, error eaque excepturi incidunt dolores quos
          at. Saepe harum debitis perspiciatis nisi optio. Consequuntur
          veritatis, laboriosam unde iste ad omnis vero neque natus ut,
          architecto atque magnam ipsum, sequi quibusdam ullam. Pariatur,
          quaerat. Mollitia, optio earum. Obcaecati aliquam ea repudiandae
          debitis distinctio iusto a unde temporibus magni, officiis ratione sit
          nemo minima voluptatibus corrupti provident quasi numquam cupiditate
          autem officia commodi laudantium alias adipisci omnis? Aspernatur
          veniam reprehenderit aliquam vitae enim cumque at facere dicta
          voluptatem ad beatae assumenda, perspiciatis ex, explicabo delectus
          quia, commodi laborum corporis officia error vero culpa? Totam
          asperiores molestias minima odit itaque dignissimos illo ipsum
          cupiditate maiores, facere dolore, optio deserunt maxime quidem
          distinctio. Soluta, inventore adipisci? Deleniti odio rerum minima,
          animi magnam error? Dignissimos adipisci officiis dicta perferendis
          neque debitis aliquid perspiciatis voluptatum natus illum eveniet,
          molestiae vel animi. Ab itaque officiis expedita fugit sapiente quas
          tenetur quisquam voluptatem excepturi doloribus ullam odit consectetur
          cum eos quibusdam odio doloremque voluptate, iusto facilis ratione
          provident pariatur? Minima vero excepturi id, doloremque ullam non
          eaque unde sunt, ratione cum autem aut. Exercitationem amet quo,
          maxime natus cupiditate commodi autem. Eum debitis reprehenderit enim
          suscipit dolores illum, neque voluptatibus quas minus? Quia, nisi
          incidunt? Optio explicabo minima qui praesentium magnam quisquam
          labore sapiente, rem unde eligendi quia aliquid porro esse ipsa quasi
          repellat cum. Sint accusamus iusto recusandae.
        </p>
        <p>
          Lorem, ipsum dolor sit amet consectetur adipisicing elit. Nam atque
          quisquam omnis deserunt aliquid minus pariatur itaque vitae voluptatum
          perferendis sapiente, animi rem eveniet non, doloribus nobis,
          architecto quod fugit blanditiis. Ut temporibus aut voluptatibus
          impedit nesciunt? Sit consectetur in veritatis aliquid velit id
          mollitia temporibus ullam. Nihil, molestiae doloremque! Ipsam
          voluptatum et nesciunt, voluptatibus dolore quidem minus sequi eveniet
          dignissimos doloremque dolorum deleniti! Iusto reiciendis eos libero?
          Pariatur molestias eligendi explicabo odit ipsam atque assumenda
          laboriosam? Omnis optio, error eaque excepturi incidunt dolores quos
          at. Saepe harum debitis perspiciatis nisi optio. Consequuntur
          veritatis, laboriosam unde iste ad omnis vero neque natus ut,
          architecto atque magnam ipsum, sequi quibusdam ullam. Pariatur,
          quaerat. Mollitia, optio earum. Obcaecati aliquam ea repudiandae
          debitis distinctio iusto a unde temporibus magni, officiis ratione sit
          nemo minima voluptatibus corrupti provident quasi numquam cupiditate
          autem officia commodi laudantium alias adipisci omnis? Aspernatur
          veniam reprehenderit aliquam vitae enim cumque at facere dicta
          voluptatem ad beatae assumenda, perspiciatis ex, explicabo delectus
          quia, commodi laborum corporis officia error vero culpa? Totam
          asperiores molestias minima odit itaque dignissimos illo ipsum
          cupiditate maiores, facere dolore, optio deserunt maxime quidem
          distinctio. Soluta, inventore adipisci? Deleniti odio rerum minima,
          animi magnam error? Dignissimos adipisci officiis dicta perferendis
          neque debitis aliquid perspiciatis voluptatum natus illum eveniet,
          molestiae vel animi. Ab itaque officiis expedita fugit sapiente quas
          tenetur quisquam voluptatem excepturi doloribus ullam odit consectetur
          cum eos quibusdam odio doloremque voluptate, iusto facilis ratione
          provident pariatur? Minima vero excepturi id, doloremque ullam non
          eaque unde sunt, ratione cum autem aut. Exercitationem amet quo,
          maxime natus cupiditate commodi autem. Eum debitis reprehenderit enim
          suscipit dolores illum, neque voluptatibus quas minus? Quia, nisi
          incidunt? Optio explicabo minima qui praesentium magnam quisquam
          labore sapiente, rem unde eligendi quia aliquid porro esse ipsa quasi
          repellat cum. Sint accusamus iusto recusandae.
        </p>
      </div>
    </DialogBase>
  );
};

const ComposableModalPlayGround: FC = () => {
  const { showModal } = useModalDrawerContext();

  return (
    <div
      className={css({
        display: 'flex',
        flexWrap: 'wrap',
        padding: '10px',
        gap: '10px',
      })}
    >
      <div>
        <h1>finish trade modals</h1>

        <ButtonBase
          onClick={() =>
            showModal(FinishTradeStatusModal, {
              transactionData: {
                __typename: 'BuyTransaction',
                asset: swapOfferMockData.targetAsset as ComponentProps<
                  typeof FinishTradeStatusModal
                >['transactionData']['asset'],
                taxAmount: { value: '0.00', precision: 2 },
                totalAmount: {
                  value: '21.54172454',
                  precision: 8,
                },
                status: 'CREATED',
                fiatAmount: {
                  value: '2406.52',
                  precision: 2,
                },
                assetAmount: swapOfferMockData.targetAmount,
                sourceAmount: swapOfferMockData.sourceAmount,
                sourceAsset: swapOfferMockData.sourceAsset as ComponentProps<
                  typeof FinishTradeStatusModal
                >['transactionData']['sourceAsset'],
              },
              hasTaxOfflineWarning: false,
            })
          }
        >
          finish swap CREATED
        </ButtonBase>
        <ButtonBase
          onClick={() =>
            showModal(FinishTradeStatusModal, {
              transactionData: {
                __typename: 'BuyTransaction',
                asset: swapOfferMockData.targetAsset as ComponentProps<
                  typeof FinishTradeStatusModal
                >['transactionData']['asset'],
                taxAmount: { value: '0.00', precision: 2 },
                totalAmount: {
                  value: '21.54172454',
                  precision: 8,
                },
                status: 'EXPIRED',
                fiatAmount: {
                  value: '2406.52',
                  precision: 2,
                },
                assetAmount: swapOfferMockData.targetAmount,
                sourceAmount: swapOfferMockData.sourceAmount,
                sourceAsset: swapOfferMockData.sourceAsset as ComponentProps<
                  typeof FinishTradeStatusModal
                >['transactionData']['sourceAsset'],
              },
              hasTaxOfflineWarning: false,
            })
          }
        >
          finish swap EXPIRED
        </ButtonBase>
        <ButtonBase
          onClick={() =>
            showModal(FinishTradeStatusModal, {
              transactionData: {
                __typename: 'BuyTransaction',
                asset: swapOfferMockData.targetAsset as ComponentProps<
                  typeof FinishTradeStatusModal
                >['transactionData']['asset'],
                taxAmount: { value: '0.00', precision: 2 },
                totalAmount: {
                  value: '21.54172454',
                  precision: 8,
                },
                status: 'FAILED',
                fiatAmount: {
                  value: '2406.52',
                  precision: 2,
                },
                assetAmount: swapOfferMockData.targetAmount,
                sourceAmount: swapOfferMockData.sourceAmount,
                sourceAsset: swapOfferMockData.sourceAsset as ComponentProps<
                  typeof FinishTradeStatusModal
                >['transactionData']['sourceAsset'],
              },
              hasTaxOfflineWarning: false,
            })
          }
        >
          finish swap FAILED
        </ButtonBase>
        <ButtonBase
          onClick={() =>
            showModal(FinishTradeStatusModal, {
              transactionData: {
                __typename: 'MetalAsset',
                asset: swapOfferMockData.targetAsset as ComponentProps<
                  typeof FinishTradeStatusModal
                >['transactionData']['asset'],
                taxAmount: { value: '0.00', precision: 2 },
                totalAmount: {
                  value: '21.54172454',
                  precision: 8,
                },
                status: 'FINISHED',
                fiatAmount: {
                  value: '2406.52',
                  precision: 2,
                },
                assetAmount: swapOfferMockData.targetAmount,
                sourceAmount: swapOfferMockData.sourceAmount,
                sourceAsset: swapOfferMockData.sourceAsset as ComponentProps<
                  typeof FinishTradeStatusModal
                >['transactionData']['sourceAsset'],
              },
              hasTaxOfflineWarning: false,
            })
          }
        >
          finish swap FINISHED
        </ButtonBase>
        <ButtonBase
          onClick={() =>
            showModal(FinishTradeStatusModal, {
              transactionData: {
                __typename: 'BuyTransaction',
                asset: swapOfferMockData.targetAsset as ComponentProps<
                  typeof FinishTradeStatusModal
                >['transactionData']['asset'],
                taxAmount: { value: '0.00', precision: 2 },
                totalAmount: {
                  value: '21.54172454',
                  precision: 8,
                },
                status: 'IN_PROGRESS',
                fiatAmount: {
                  value: '2406.52',
                  precision: 2,
                },
                assetAmount: swapOfferMockData.targetAmount,
                sourceAmount: swapOfferMockData.sourceAmount,
                sourceAsset: swapOfferMockData.sourceAsset as ComponentProps<
                  typeof FinishTradeStatusModal
                >['transactionData']['sourceAsset'],
              },
              hasTaxOfflineWarning: false,
            })
          }
        >
          finish swap IN_PROGRESS
        </ButtonBase>
      </div>
      <ButtonBase
        onClick={() =>
          showModal(SavingsPlanCancelModal, {
            savingsPlan: {
              id: 'savings-plan-id-123',
              name: 'My Super Savings Plan',
            },
            translations: {
              cancelPlan: 'Cancel plan',
              cancelPlanConfirmationDescription:
                'Are you sure you want to cancel your savings plan?',
              cancelPlanConfirmationTitle: 'Cancel savings plan',
              goBack: 'Go back',
              cancelPlanInProgressConfirmationDescription:
                'Your savings plan will be cancelled. If a payment is already in progress, the related trade will still be executed. The cancellation will take effect afterwards.',
            },
          })
        }
      >
        Savings plan cancel modal
      </ButtonBase>
      <ButtonBase
        onClick={() =>
          showModal(CryptoTransferTransactionStatusModal, {
            status: 'PENDING',
            transactionType: 'DEPOSIT_CRYPTO_TRANSFERS',
          })
        }
      >
        pending transfer transaction status modal
      </ButtonBase>
      <ButtonBase onClick={() => showModal(ModalComponentWithModalChild)}>
        nested modals
      </ButtonBase>
      <ButtonBase
        onClick={() =>
          showModal(WarningModal, {
            title: 'Are you sure you want to cancel this trade?',
            cancelBtnText: "No, don't cancel",
            continueLink: {
              text: 'Yes, go to portfolio',
              url: '/portfolio',
            },
          })
        }
      >
        cancel trade modal
      </ButtonBase>
      <ButtonBase onClick={() => showModal(SetupFATCAModal)}>
        Setup FATCA modal
      </ButtonBase>
      <ButtonBase onClick={() => showModal(TriggerSetupTwoFaModal)}>
        Setup trigger logout Two FA Modal
      </ButtonBase>
      <ButtonBase
        onClick={() =>
          showModal(ResetTwoFaModal, {
            disableOverlayClose: true,
            translations: mockTransactions,
          })
        }
      >
        Reset Two FA Modal
      </ButtonBase>
      <ButtonBase
        onClick={() =>
          showModal(TriggerResetTwoFaModal, {
            disableOverlayClose: true,
          })
        }
      >
        Trigger Reset Two FA Modal
      </ButtonBase>
      <ButtonBase
        onClick={() =>
          showModal(SetupTwoFaModal, {
            disableOverlayClose: true,
            translations: mockTransactions,
          })
        }
      >
        Setup Two FA Modal
      </ButtonBase>
      <ButtonBase onClick={() => showModal(VeryTallModal)}>
        very tall modal
      </ButtonBase>
      <ButtonBase
        onClick={() =>
          showModal(UserAccessModal, {
            type: AccessInfoType.InfoApprovalPending,
          })
        }
      >
        User access : Info Approval Pending
      </ButtonBase>
      <ButtonBase
        onClick={() =>
          showModal(UserAccessModal, {
            type: AccessInfoType.ReadOnly,
          })
        }
      >
        User access : read only
      </ButtonBase>
      <ButtonBase
        onClick={() =>
          showModal(UserAccessModal, {
            type: AccessInfoType.WarningTrade,
            disableOverlayClose: true,
          })
        }
      >
        User access : warning trade
      </ButtonBase>
      <ButtonBase
        onClick={() =>
          showModal(UserAccessModal, {
            type: AccessInfoType.WarningTrade,
          })
        }
      >
        User access : warning transfer
      </ButtonBase>
    </div>
  );
};

export { ComposableModalPlayGround };
